/**
 * The lesson-journey progress widget: a row of 25 stars over the lesson's words, a rocket
 * travelling toward the ABC Planet, and a caption. See specs/word-mastery-progression.md §6.
 *
 * The 25 stars track `completion` (mastered / lesson size), so the bar fills evenly whatever the
 * lesson's word count; the rocket rides `completion` and reaching all 25 stars = the ABC Planet.
 *
 * Pure Phaser graphics + ★/☆ glyphs — no image assets, matches the emoji-based HUD hearts.
 */
import Phaser from 'phaser';
import i18next from 'i18next';
import { FONT_STACK } from '@/game/config';
import { STAR_COUNT, starsFor } from '@/game/progression/config';
import { createDailyLifePlanet, type PlanetHandle } from '@/game/planet';
import { STARS, type StarGlyphs } from '@/game/starGlyphs';
import type { PoolStats } from '@/game/progression/ReadingProgression';

/** Reward icon (Pack.reward.icon) that renders the procedural growing earth planet instead of an image. */
const GROWING_PLANET_ICON = 'earth_planet';

/** How far the reward target swells on each breath of its pulse. See pulseTarget(). */
const PULSE_MULT = 1.15;

/**
 * The pulse's half-period, ms — one breath in, one breath out, matching the Play button's 2.4s
 * `menu-play-glow` animation so the menu has one heartbeat rather than two.
 */
const PULSE_MS = 1200;

/** Shared radial-gradient sprite behind a pulsing target, built on first use (see ensureGlow). */
const GLOW_TEXTURE = 'reward-target-glow';
const GLOW_PX = 256;

const COLOR_TRACK = '#2a3038';
const COLOR_TRACK_DONE = '#7ec4ff';
const COLOR_PLANET = '#8b6fe0';

export interface ProgressBarOptions {
  width?: number;       // total widget width in px (default 900)
  starSize?: number;    // star glyph font size (default 26)
  showCaption?: boolean; // render the "x / 250 mastered" line (default true)
  rewardIcon?: string;  // texture key for the end-of-journey target (default 'abc_star')
  rewardLabelKey?: string; // i18next key for the "journey complete" caption (default 'journey.planetReached')
  /** The glyphs the 25 journey stars are drawn with (default ★/☆; Halloween counts spiders). */
  glyphs?: StarGlyphs;
  /**
   * Makes the reward target at the end of the track tappable — the Scoreboard's entry point
   * (specs/scoreboard.md §5). Omitted, the bar is exactly what it was: nothing on it is
   * interactive. Wired by the Menu only, never by the Summary, where the bar is mid-animation and
   * sits under a full-screen "any tap goes back to the menu" catcher.
   */
  onTargetClick?: () => void;
}

export class ProgressBar extends Phaser.GameObjects.Container {
  private readonly barWidth: number;
  private readonly trackY: number;
  private readonly trackLeft: number;
  private readonly trackRight: number;

  private readonly stars: Phaser.GameObjects.Text[] = [];
  private readonly glyphs: StarGlyphs;
  private readonly trackFill: Phaser.GameObjects.Rectangle;
  private readonly rocket: Phaser.GameObjects.GameObject & { x: number; setAngle?: (a: number) => unknown };
  private readonly target: Phaser.GameObjects.GameObject;
  /** Where the reward target sits and how big it is — the pulse's glow is drawn to match. */
  private readonly targetX: number;
  private readonly targetR: number;
  /**
   * The pulse the caller asked for, and the tweens currently running it. The list empties while a
   * pop borrows the same scale, and fills again when the pop is done (see popTarget).
   */
  private pulseOn = false;
  private pulse: Phaser.Tweens.Tween[] = [];
  /** Soft halo behind the target, built only if the target ever pulses. Hidden at alpha 0. */
  private glow: Phaser.GameObjects.Image | null = null;
  private glowScale = 1;
  /**
   * The target art's RESTING scale, captured once — every pop of the icon must be measured against
   * it (see popTarget). Rarely 1: the reward images are sized with `setDisplaySize`, which leaves an
   * Image sitting at about 0.3, so a tween to a literal `1.15` does not pop the icon, it inflates it
   * nearly fourfold. The two container cases DO rest at 1, which is what hid this for so long.
   */
  private readonly targetScale: number;
  private readonly caption?: Phaser.GameObjects.Text;
  private readonly rewardLabelKey: string;
  /** Set when the reward is the procedural growing earth planet (Daily Life journey). */
  private readonly planet?: PlanetHandle;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: ProgressBarOptions = {}) {
    super(scene, x, y);
    scene.add.existing(this);

    this.barWidth = opts.width ?? 900;
    this.rewardLabelKey = opts.rewardLabelKey ?? 'journey.planetReached';
    this.glyphs = opts.glyphs ?? STARS;
    const rewardIcon = opts.rewardIcon ?? 'abc_star';
    const starSize = opts.starSize ?? 26;
    const planetR = 34;
    const half = this.barWidth / 2;
    this.trackLeft = -half + 10;
    this.trackRight = half - planetR * 2 - 16;   // leave room for the planet on the right
    this.trackY = 0;

    // Track line (background + travelled fill).
    const trackBg = scene.add.rectangle(
      (this.trackLeft + this.trackRight) / 2, this.trackY,
      this.trackRight - this.trackLeft, 6, Phaser.Display.Color.HexStringToColor(COLOR_TRACK).color,
    );
    this.trackFill = scene.add.rectangle(this.trackLeft, this.trackY, 0, 6,
      Phaser.Display.Color.HexStringToColor(COLOR_TRACK_DONE).color).setOrigin(0, 0.5);

    // Stars spaced evenly along the track.
    for (let i = 0; i < STAR_COUNT; i++) {
      const sx = this.trackLeft + ((i + 0.5) / STAR_COUNT) * (this.trackRight - this.trackLeft);
      const star = scene.add.text(sx, this.trackY, this.glyphs.empty, {
        fontFamily: FONT_STACK, fontSize: `${starSize}px`, color: this.glyphs.emptyColor,
      }).setOrigin(0.5);
      this.stars.push(star);
    }

    // Journey target on the right (ABC Star, or the lesson's reward like the Unicorn Star): the
    // procedural growing earth planet (Daily Life), the generated logo if loaded, else a disc.
    const targetX = half - planetR - 4;
    this.targetX = targetX;
    this.targetR = planetR;
    if (rewardIcon === GROWING_PLANET_ICON) {
      this.planet = createDailyLifePlanet(scene, targetX, this.trackY, planetR);
      this.target = this.planet.container;
    } else if (scene.textures.exists(rewardIcon)) {
      const star = scene.add.image(targetX, this.trackY, rewardIcon).setOrigin(0.5);
      star.setDisplaySize(planetR * 2.2, planetR * 2.2);
      this.target = star;
    } else {
      const planet = scene.add.container(targetX, this.trackY);
      const circle = scene.add.circle(0, 0, planetR, Phaser.Display.Color.HexStringToColor(COLOR_PLANET).color)
        .setStrokeStyle(3, 0xffffff);
      const label = scene.add.text(0, 0, 'ABC', {
        fontFamily: FONT_STACK, fontSize: '18px', color: '#ffffff', fontStyle: 'bold',
      }).setOrigin(0.5);
      planet.add([circle, label]);
      this.target = planet;
    }

    this.targetScale = (this.target as Phaser.GameObjects.Image).scale ?? 1;

    // Rocket travelling along the track: the generated sprite if loaded, else a triangle glyph.
    if (scene.textures.exists('rocket')) {
      const r = scene.add.image(this.trackLeft, this.trackY - 18, 'rocket').setOrigin(0.5);
      r.setDisplaySize(40, 40);
      this.rocket = r;
    } else {
      this.rocket = scene.add.text(this.trackLeft, this.trackY - 22, '▲', {
        fontFamily: FONT_STACK, fontSize: '22px', color: '#ffffff',
      }).setOrigin(0.5).setAngle(90);
    }

    this.add([trackBg, this.trackFill, ...this.stars, this.target, this.rocket]);

    // The target as a button (specs/scoreboard.md §5). A separate ZONE rather than
    // `setInteractive()` on the art itself, because the art is one of three different objects
    // depending on the lesson (an Image, the procedural earth container, or the fallback container)
    // — and because a container's hit area is where this codebase has been bitten before: a
    // `setSize` shifted `displayOrigin` and left the help button's lower half dead (see CLAUDE.md).
    // A zone sized on its own has neither problem, and it is 20% larger than the art so a small
    // finger lands on it.
    if (opts.onTargetClick) {
      const hit = scene.add.zone(targetX, this.trackY, planetR * 2.4, planetR * 2.4)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => {
        // A press the child can see on the icon itself — the zone is invisible, so without this
        // nothing acknowledges the tap until the dialog opens.
        this.popTarget(1.12, 110);
        opts.onTargetClick!();
      });
      this.add(hit);
    }

    if (opts.showCaption ?? true) {
      this.caption = scene.add.text(0, this.trackY + 34, '', {
        fontFamily: FONT_STACK, fontSize: '18px', color: '#aabbcc',
      }).setOrigin(0.5, 0);
      this.add(this.caption);
    }
  }

  /**
   * Pop the reward target to `mult` times its RESTING scale and back — the arrival celebration and
   * the tap acknowledgement, which are the only two things that touch its scale.
   *
   * Relative, never absolute: see the note on `targetScale`. Any pop still in flight is killed and
   * the icon snapped back first, so two overlapping pops (a tap during the celebration, or a double
   * tap) cannot leave the art stranded at a size nothing will restore. A running pulse is stood
   * down for the duration and picked up again afterwards, unless it was switched off meanwhile.
   */
  private popTarget(mult: number, duration: number, repeat = 0): void {
    this.killPulseTween();
    this.scene.tweens.killTweensOf(this.target);
    (this.target as Phaser.GameObjects.Image).setScale(this.targetScale);
    this.scene.tweens.add({
      targets: this.target, scale: this.targetScale * mult,
      duration, yoyo: true, repeat, ease: 'Quad.easeOut',
      onComplete: () => {
        (this.target as Phaser.GameObjects.Image).setScale(this.targetScale);
        this.startPulseTween();
      },
    });
  }

  /**
   * A slow breath on the reward target — it grows by PULSE_MULT and settles back, forever, with a
   * pause between breaths.
   *
   * The icon opens the record card and nothing on the menu says so; it has looked like decoration
   * since the journey bar shipped. The menu turns this on when the lesson's records have moved
   * since the card was last read (specs/scoreboard.md §5.5), which is both the moment there is
   * something new to see and the only moment a nudge is honest.
   *
   * Small on purpose. A big pulse on a static screen reads as an error state, and the target sits
   * two pixels from the reset link — this has to invite a tap, not demand one.
   */
  pulseTarget(): void {
    this.pulseOn = true;
    this.startPulseTween();
  }

  /**
   * Stop the pulse and settle the icon. Called once the card has been opened — the invitation was
   * accepted, and a target that keeps breathing afterwards is asking for a tap that does nothing
   * new.
   */
  stopTargetPulse(): void {
    this.pulseOn = false;
    this.killPulseTween();
    (this.target as Phaser.GameObjects.Image).setScale(this.targetScale);
  }

  /**
   * The halo under a pulsing target: the Play button's glow, quieter. That button has breathed a
   * soft blue `box-shadow` at the middle of this menu since it shipped (`menu-play-glow` in
   * index.html), so the one other thing on the screen asking to be tapped should look like it and
   * not like a second, unrelated idea — at a lower peak alpha, because the target is an invitation
   * and the Play button is the point of the screen.
   *
   * A real radial gradient, baked once into a shared canvas texture: the previous attempt stacked
   * two flat translucent discs, which is how every other soft light in this codebase is drawn, and
   * at this size the disc edges were plainly visible — a grey plate behind the icon rather than a
   * glow. Additive blending is what makes it read as light on the dark menu.
   *
   * Not a WebGL glow effect: the game also runs on the Canvas renderer inside the iOS WKWebView,
   * where an FX pipeline is silently a no-op, and a cue invisible on one of the three platforms is
   * not a cue.
   */
  private ensureGlow(): void {
    if (this.glow) return;
    if (!this.scene.textures.exists(GLOW_TEXTURE)) {
      const canvas = this.scene.textures.createCanvas(GLOW_TEXTURE, GLOW_PX, GLOW_PX);
      const ctx = canvas?.getContext();
      if (ctx) {
        const r = GLOW_PX / 2;
        // Held near full out to a fifth of the radius so the icon sits INSIDE the light rather
        // than on a bright ring, then all the way to nothing at the edge.
        const g = ctx.createRadialGradient(r, r, r * 0.2, r, r, r);
        g.addColorStop(0, 'rgba(126, 196, 255, 0.9)');
        g.addColorStop(0.45, 'rgba(126, 196, 255, 0.28)');
        g.addColorStop(1, 'rgba(126, 196, 255, 0)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, GLOW_PX, GLOW_PX);
        canvas?.refresh();
      }
    }
    const img = this.scene.add.image(this.targetX, this.trackY, GLOW_TEXTURE).setOrigin(0.5);
    // Wide, like the button's 40px blur: the light has to spill well past the art or it reads as a
    // ring around it. Scale is captured because the pulse tweens it — setDisplaySize would fight.
    this.glowScale = (this.targetR * 5.2) / GLOW_PX;
    img.setScale(this.glowScale).setAlpha(0).setBlendMode(Phaser.BlendModes.ADD);
    // Directly behind the target, not at the bottom of the container: the track fill and the rocket
    // pass under the reward icon, and a glow beneath them would light them up instead of it.
    this.addAt(img, Math.max(0, this.list.indexOf(this.target)));
    this.glow = img;
  }

  private startPulseTween(): void {
    if (!this.pulseOn || this.pulse.length > 0) return;
    this.ensureGlow();
    // One rhythm, two tweens: the icon breathes on its own resting scale, the plate behind it
    // brightens and spreads on the same clock. Splitting them is what lets each move on the
    // property that suits it without either one owning the other's numbers.
    const beat = { duration: PULSE_MS, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } as const;
    this.pulse = [
      this.scene.tweens.add({ targets: this.target, scale: this.targetScale * PULSE_MULT, ...beat }),
    ];
    if (this.glow) {
      // Never fully out and never bright: the halo hangs at a low alpha and swells on the same
      // clock as the icon, which is what the Play button does with its blur radius.
      this.pulse.push(this.scene.tweens.add({
        targets: this.glow,
        alpha: { from: 0.22, to: 0.6 },
        scale: { from: this.glowScale * 0.88, to: this.glowScale * 1.14 },
        ...beat,
      }));
    }
  }

  /** One journey star, lit or dark, in this lesson's glyphs. */
  private paintStar(star: Phaser.GameObjects.Text, filled: boolean): void {
    star.setText(filled ? this.glyphs.full : this.glyphs.empty);
    star.setColor(filled ? this.glyphs.fullColor : this.glyphs.emptyColor);
    // Colour emoji ignore the text colour, so a dark spider needs a halo to read on the dark menu.
    if (this.glyphs.halo && filled) star.setShadow(0, 0, this.glyphs.halo, 10, true, true);
    else star.setShadow(0, 0, '#000000', 0, false, false);
  }

  private killPulseTween(): void {
    for (const t of this.pulse) t.remove();
    this.pulse = [];
    this.glow?.setAlpha(0).setScale(this.glowScale);
  }

  /** Update visuals from stats. `animate` tweens the rocket/fill (used after a round). */
  setStats(stats: PoolStats, animate = false): void {
    for (let i = 0; i < this.stars.length; i++) {
      const filled = i < stats.stars;
      this.paintStar(this.stars[i], filled);
    }
    this.planet?.setStars(stats.stars, STAR_COUNT);

    const trackW = this.trackRight - this.trackLeft;
    const targetFill = Math.max(0, Math.min(1, stats.completion)) * trackW;
    const targetRocketX = this.trackLeft + targetFill;

    if (animate) {
      this.scene.tweens.add({ targets: this.trackFill, width: targetFill, duration: 700, ease: 'Cubic.easeOut' });
      this.scene.tweens.add({ targets: this.rocket, x: targetRocketX, duration: 700, ease: 'Cubic.easeOut' });
    } else {
      this.trackFill.width = targetFill;
      this.rocket.x = targetRocketX;
    }

    if (stats.abcPlanetReached) {
      // Celebrate arrival: pop the target a touch larger.
      this.popTarget(1.15, 500, 1);
    }

    if (this.caption) {
      const text = stats.abcPlanetReached
        ? i18next.t(this.rewardLabelKey)
        : i18next.t('journey.mastered', { n: stats.mastered, total: stats.total })
          + `   ${this.glyphs.full} ${stats.stars}/${STAR_COUNT}`;
      this.caption.setText(text);
    }
  }

  /**
   * Celebrate newly mastered words: snap the bar to `from`, then drop one wiggling star per
   * gained word from the top of the screen onto the rocket lane. Each landing nudges the rocket
   * forward and lights up any star milestone it crosses; the bar ends exactly on `to`. Used by
   * the end-of-round summary and, on a mid-round back-out, the menu's journey bar.
   */
  animateGains(from: PoolStats, to: PoolStats, onComplete?: () => void): void {
    this.setStats(from, false);
    const gained = Math.max(0, to.mastered - from.mastered);
    if (gained === 0) { onComplete?.(); return; }

    // One star per word, capped so a big haul doesn't run too long; the rocket still travels
    // the full distance because each star carries gained/numStars words.
    const numStars = Math.min(gained, 18);
    const stagger = Math.min(170, Math.floor(1700 / numStars));
    const masteredAfter = (i: number) => from.mastered + Math.round(((i + 1) / numStars) * gained);

    let landed = 0;
    for (let i = 0; i < numStars; i++) {
      this.scene.time.delayedCall(i * stagger, () => {
        this.dropStar(masteredAfter(i), to, () => {
          if (++landed === numStars) {
            // Let the last rocket nudge glide, then settle on the exact final state (and fire
            // the ABC-Planet celebration if it was reached this session).
            this.scene.time.delayedCall(320, () => { this.setStats(to, false); onComplete?.(); });
          }
        });
      });
    }
  }

  /** Drops a single wiggling star from the top of the screen to the rocket lane, then lands it. */
  private dropStar(masteredAfter: number, finalStats: PoolStats, onLand: () => void): void {
    const trackW = this.trackRight - this.trackLeft;
    const localTargetX = this.trackLeft + Math.max(0, Math.min(1, masteredAfter / finalStats.total)) * trackW;
    const worldTargetX = this.x + localTargetX;
    const worldTargetY = this.y + this.trackY - 18;   // the rocket lane
    const worldStartX = this.x + Phaser.Math.Between(Math.round(this.trackLeft), Math.round(this.trackRight));

    // Scene-level (not a container child) at high depth so it falls visibly over everything.
    const star = this.scene.add.text(worldStartX, 30, this.glyphs.full, {
      fontFamily: FONT_STACK, fontSize: '30px', color: this.glyphs.fullColor,
    }).setOrigin(0.5).setDepth(1000);
    if (this.glyphs.halo) star.setShadow(0, 0, this.glyphs.halo, 10, true, true);

    // Wiggle: rock side to side the whole way down.
    const wiggle = this.scene.tweens.add({
      targets: star, angle: { from: -18, to: 18 },
      duration: 130, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
    // Fall: accelerate (gravity-like) toward the landing point on the bar.
    this.scene.tweens.add({
      targets: star, x: worldTargetX, y: worldTargetY,
      duration: 780, ease: 'Quad.easeIn',
      onComplete: () => {
        wiggle.stop();
        this.scene.tweens.add({                       // pop + fade on impact
          targets: star, scale: 1.7, alpha: 0, angle: 0,
          duration: 220, ease: 'Quad.easeOut', onComplete: () => star.destroy(),
        });
        this.onStarLanded(masteredAfter, finalStats);
        onLand();
      },
    });
  }

  /** Reacts to a star hitting the bar: nudge the rocket forward and fill any newly reached star. */
  private onStarLanded(mastered: number, finalStats: PoolStats): void {
    const trackW = this.trackRight - this.trackLeft;
    const fill = Math.max(0, Math.min(1, mastered / finalStats.total)) * trackW;
    const rocketX = this.trackLeft + fill;
    this.scene.tweens.add({ targets: this.trackFill, width: fill, duration: 300, ease: 'Cubic.easeOut' });
    this.scene.tweens.add({ targets: this.rocket, x: rocketX, duration: 300, ease: 'Back.easeOut' });
    // A little hop sells the "rocket moves up" on each hit.
    this.scene.tweens.add({
      targets: this.rocket, y: this.trackY - 28, duration: 150, yoyo: true, ease: 'Quad.easeOut',
    });

    const starsNow = starsFor(mastered, finalStats.total);
    this.planet?.setStars(starsNow, STAR_COUNT);
    for (let i = 0; i < this.stars.length; i++) {
      const filled = i < starsNow;
      const wasFilled = this.stars[i].text === this.glyphs.full;
      this.paintStar(this.stars[i], filled);
      if (filled && !wasFilled) {
        this.scene.tweens.add({ targets: this.stars[i], scale: 1.6, duration: 220, yoyo: true, ease: 'Quad.easeOut' });
      }
    }

    if (this.caption) {
      this.caption.setText(
        i18next.t('journey.mastered', { n: mastered, total: finalStats.total })
          + `   ${this.glyphs.full} ${starsNow}/${STAR_COUNT}`,
      );
    }
  }
}
